import React, { useState } from 'react';
import {
  Mail,
  Server,
  Shield,
  Send,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Save,
  TestTube,
  Edit3,
  FileText,
  ChevronDown
} from 'lucide-react';
import { useQuery, useMutation, gql } from '@/apollo';
import { apolloClient } from '../../services/graphql';
import { useAuth } from '../../contexts/AuthContext';
import { useApplicationSettings } from '../../contexts/ApplicationSettingsContext';
import { EmailTiptapEditor } from '../ui/EmailTiptapEditor';

// GraphQL Queries and Mutations
const GET_EMAIL_CONFIGURATION = gql`
  query GetEmailConfiguration {
    emailConfiguration {
      id
      smtpHost
      smtpPort
      smtpSecure
      smtpUser
      fromEmail
      fromName
      welcomeSubject
      welcomeTemplate
      passwordChangeSubject
      passwordChangeTemplate
      passwordResetSubject
      passwordResetTemplate
      loginMfaSubject
      loginMfaTemplate
      loginLockoutSubject
      loginLockoutTemplate
      accountDeactivatedSubject
      accountDeactivatedTemplate
      isEnabled
      lastTestedAt
      testEmailSent
      createdAt
      updatedAt
    }
    emailConnectionStatus {
      isConfigured
      isConnected
      lastTestedAt
      error
    }
  }
`;

const UPDATE_EMAIL_CONFIGURATION = gql`
  mutation UpdateEmailConfiguration($input: UpdateEmailConfigurationInput!) {
    updateEmailConfiguration(input: $input) {
      id
      smtpHost
      smtpPort
      smtpSecure
      smtpUser
      fromEmail
      fromName
      welcomeSubject
      welcomeTemplate
      passwordChangeSubject
      passwordChangeTemplate
      passwordResetSubject
      passwordResetTemplate
      loginMfaSubject
      loginMfaTemplate
      loginLockoutSubject
      loginLockoutTemplate
      accountDeactivatedSubject
      accountDeactivatedTemplate
      isEnabled
      lastTestedAt
      testEmailSent
      updatedAt
    }
  }
`;

const SEND_TEST_EMAIL = gql`
  mutation SendTestEmail($to: String!) {
    sendTestEmail(to: $to) {
      success
      message
      sentAt
    }
  }
`;

const TEST_EMAIL_CONNECTION = gql`
  query TestEmailConnection {
    emailConnectionStatus {
      isConfigured
      isConnected
      lastTestedAt
      error
    }
  }
`;

interface EmailConfiguration {
  id: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser?: string;
  fromEmail?: string;
  fromName: string;
  welcomeSubject: string;
  welcomeTemplate: string;
  passwordChangeSubject: string;
  passwordChangeTemplate: string;
  passwordResetSubject: string;
  passwordResetTemplate: string;
  loginMfaSubject: string;
  loginMfaTemplate: string;
  loginLockoutSubject: string;
  loginLockoutTemplate: string;
  accountDeactivatedSubject: string;
  accountDeactivatedTemplate: string;
  isEnabled: boolean;
  lastTestedAt?: string;
  testEmailSent?: string;
  createdAt: string;
  updatedAt: string;
}

type TemplateType = 'welcome' | 'password-change' | 'password-reset' | 'login-mfa' | 'login-lockout' | 'account-deactivated';

const TEMPLATE_CONFIG: Record<
  TemplateType,
  {
    label: string;
    subjectKey: keyof EmailConfiguration;
    bodyKey: keyof EmailConfiguration;
    subjectPlaceholder: string;
    bodyPlaceholder: string;
  }
> = {
  welcome: {
    label: 'Welcome Email',
    subjectKey: 'welcomeSubject',
    bodyKey: 'welcomeTemplate',
    subjectPlaceholder: 'Welcome to {companyName}',
    bodyPlaceholder: 'Welcome {firstName}! Your account has been created. Your temporary password is {password}.',
  },
  'password-change': {
    label: 'Password Change Notification',
    subjectKey: 'passwordChangeSubject',
    bodyKey: 'passwordChangeTemplate',
    subjectPlaceholder: 'Your new password',
    bodyPlaceholder:
      'Used for password-change notifications. Admin password resets use a fixed credentials email layout.',
  },
  'password-reset': {
    label: 'Password Reset',
    subjectKey: 'passwordResetSubject',
    bodyKey: 'passwordResetTemplate',
    subjectPlaceholder: 'Password Reset Instructions',
    bodyPlaceholder: 'Click the link to reset your password: {resetLink}',
  },
  'login-mfa': {
    label: 'Login Verification Code',
    subjectKey: 'loginMfaSubject',
    bodyKey: 'loginMfaTemplate',
    subjectPlaceholder: 'Your sign-in verification code',
    bodyPlaceholder: 'Hello {firstName}, your verification code is {code}. It expires in 10 minutes.',
  },
  'login-lockout': {
    label: 'Login Lockout',
    subjectKey: 'loginLockoutSubject',
    bodyKey: 'loginLockoutTemplate',
    subjectPlaceholder: 'Your account has been temporarily locked',
    bodyPlaceholder: 'Hello {firstName}, your account was temporarily locked after too many failed sign-in attempts. Try again in {lockoutMinutes} minutes.',
  },
  'account-deactivated': {
    label: 'Account Deactivated (Login)',
    subjectKey: 'accountDeactivatedSubject',
    bodyKey: 'accountDeactivatedTemplate',
    subjectPlaceholder: 'Your account has been deactivated',
    bodyPlaceholder: 'Hello {firstName}, your account was deactivated after too many failed sign-in attempts within {deactivationWindowMinutes} minutes. Contact an administrator to restore access.',
  },
};

export const EmailConfigurationDashboard: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { settings } = useApplicationSettings();
  
  const [activeTab, setActiveTab] = useState<'settings' | 'templates' | 'testing'>('settings');
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [formData, setFormData] = useState<Partial<EmailConfiguration>>({});
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('welcome');
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState<{
    isConnected: boolean;
    error: string | null;
  } | null>(null);

  const isAdmin = !!currentUser && currentUser.role === 'ADMINISTRATOR';

  const { data, loading, error, refetch } = useQuery(GET_EMAIL_CONFIGURATION, {
    skip: !isAdmin,
    fetchPolicy: 'network-only',
  });

  React.useEffect(() => {
    if (data?.emailConfiguration) {
      setFormData(data.emailConfiguration);
    }
  }, [data]);

  const [updateEmailConfiguration, { loading: isSaving }] = useMutation(UPDATE_EMAIL_CONFIGURATION, {
    refetchQueries: [{ query: GET_EMAIL_CONFIGURATION }],
  });

  const [sendTestEmail, { loading: isSendingTest, data: testEmailResult }] = useMutation(SEND_TEST_EMAIL, {
    refetchQueries: [{ query: GET_EMAIL_CONFIGURATION }],
  });

  const handleSave = async () => {
    // Only send fields that are allowed in UpdateEmailConfigurationInput
    const allowedFields = [
      'smtpHost',
      'smtpPort',
      'smtpSecure',
      'smtpUser',
      'smtpPassword',
      'fromEmail',
      'fromName',
      'welcomeSubject',
      'welcomeTemplate',
      'passwordChangeSubject',
      'passwordChangeTemplate',
      'passwordResetSubject',
      'passwordResetTemplate',
      'loginMfaSubject',
      'loginMfaTemplate',
      'loginLockoutSubject',
      'loginLockoutTemplate',
      'accountDeactivatedSubject',
      'accountDeactivatedTemplate',
      'isEnabled'
    ];
    
    const input = Object.keys(formData)
      .filter(key => allowedFields.includes(key))
      .reduce((obj: any, key) => {
        obj[key] = formData[key as keyof EmailConfiguration];
        return obj;
      }, {} as Partial<EmailConfiguration>);
    
    await updateEmailConfiguration({ variables: { input } });
    
    // Automatically test connection after saving if email is enabled
    if (input.isEnabled) {
      handleTestConnection();
    }
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setConnectionTestResult(null);
    
    try {
      const { data } = await apolloClient.query({
        query: TEST_EMAIL_CONNECTION,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      });
      
      const status = data?.emailConnectionStatus;
      setConnectionTestResult({
        isConnected: status?.isConnected || false,
        error: status?.error || null
      });
    } catch (error: any) {
      setConnectionTestResult({
        isConnected: false,
        error: error.message || 'Connection test failed'
      });
    } finally {
      setIsTestingConnection(false);
      // Refetch configuration to get updated status
      await refetch();
    }
  };

  const handleTestEmail = () => {
    if (testEmail) {
      sendTestEmail({ variables: { to: testEmail } });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Access Denied</h3>
        <p style={{ color: 'var(--color-text-secondary)' }}>Only administrators can access email configuration.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading email configuration...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Error Loading Configuration</h3>
        <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>Failed to load email configuration. Please try again.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // const config = data?.emailConfiguration; // Reserved for future use
  const connectionStatus = data?.emailConnectionStatus;

  const tabs = [
    { id: 'settings', label: 'SMTP Settings', icon: Server },
    { id: 'templates', label: 'Email Templates', icon: FileText },
    { id: 'testing', label: 'Testing', icon: TestTube },
  ];

  // Determine current connection status (use test result if available, otherwise use query data)
  const currentConnectionStatus = connectionTestResult 
    ? { isConnected: connectionTestResult.isConnected, error: connectionTestResult.error }
    : connectionStatus;

  return (
    <div className="space-y-6">
      {/* Connection Test Status Banner */}
      {isTestingConnection && (
        <div 
          className="border rounded-lg p-4 animate-pulse"
          style={{
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderColor: 'rgba(59, 130, 246, 0.3)'
          }}
        >
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 mr-3" style={{ borderColor: 'var(--color-accent)' }}></div>
            <div>
              <h4 className="font-semibold" style={{ color: 'var(--color-accent)' }}>Testing Email Connection...</h4>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                Please wait while we verify your SMTP configuration. This may take up to 2 minutes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Connection Test Result Banner */}
      {!isTestingConnection && connectionTestResult && (
        <div 
          className="border rounded-lg p-4"
          style={{
            backgroundColor: connectionTestResult.isConnected 
              ? 'rgba(34, 197, 94, 0.1)' 
              : 'rgba(239, 68, 68, 0.1)',
            borderColor: connectionTestResult.isConnected 
              ? 'rgba(34, 197, 94, 0.3)' 
              : 'rgba(239, 68, 68, 0.3)'
          }}
        >
          <div className="flex items-start">
            {connectionTestResult.isConnected ? (
              <CheckCircle className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" style={{ color: 'var(--color-success)' }} />
            ) : (
              <AlertCircle className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" style={{ color: 'var(--color-error)' }} />
            )}
            <div className="flex-1">
              <h4 className="font-semibold" style={{
                color: connectionTestResult.isConnected ? 'var(--color-success)' : 'var(--color-error)'
              }}>
                {connectionTestResult.isConnected ? 'Connection Successful!' : 'Connection Failed'}
              </h4>
              {connectionTestResult.error && (
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {connectionTestResult.error}
                </p>
              )}
            </div>
            <button
              onClick={() => setConnectionTestResult(null)}
              className="text-sm px-3 py-1 rounded transition-colors"
              style={{ 
                color: 'var(--color-text-muted)',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--color-text-secondary)';
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--color-text-muted)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
            <Mail className="w-7 h-7 mr-3" style={{ color: 'var(--color-accent)' }} />
            Email Configuration
          </h2>
          <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Configure SMTP settings, email templates, and test email delivery
          </p>
        </div>
        
        {/* Status Indicator */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            {currentConnectionStatus?.isConnected ? (
              <CheckCircle className="w-5 h-5 mr-2" style={{ color: 'var(--color-success)' }} />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" style={{ color: 'var(--color-error)' }} />
            )}
            <span className="text-sm font-medium" style={{
              color: currentConnectionStatus?.isConnected ? 'var(--color-success)' : 'var(--color-error)'
            }}>
              {currentConnectionStatus?.isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          
          <button
            onClick={handleTestConnection}
            disabled={isTestingConnection || !formData.isEnabled}
            className="flex items-center px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
            style={{
              backgroundColor: 'var(--color-info, #06b6d4)',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              if (!isTestingConnection && formData.isEnabled) {
                e.currentTarget.style.backgroundColor = 'var(--color-info-hover, #0891b2)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isTestingConnection && formData.isEnabled) {
                e.currentTarget.style.backgroundColor = 'var(--color-info, #06b6d4)';
              }
            }}
          >
            {isTestingConnection ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Testing...
              </>
            ) : (
              <>
                <TestTube className="w-4 h-4 mr-2" />
                Test Connection
              </>
            )}
          </button>
          
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.backgroundColor = 'var(--color-accent-hover, #3b82f6)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              }
            }}
          >
            {isSaving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </button>
        </div>
      </div>

      {/* Connection Status Card - Only show if not testing and not showing test result */}
      {!isTestingConnection && !connectionTestResult && !currentConnectionStatus?.isConnected && formData.isEnabled && (
        <div 
          className="border rounded-lg p-4"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderColor: 'rgba(239, 68, 68, 0.3)'
          }}
        >
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" style={{ color: 'var(--color-error)' }} />
            <div className="flex-1">
              <h4 className="font-semibold mb-1" style={{ color: 'var(--color-error)' }}>Connection Issue</h4>
              <p className="text-sm mt-1" style={{ color: 'var(--color-error)' }}>
                {currentConnectionStatus?.error || 'Connection failed'}
              </p>
              {currentConnectionStatus?.lastTestedAt && (
                <p className="text-xs mt-2" style={{ color: 'var(--color-error)' }}>
                  Last tested: {formatDate(currentConnectionStatus.lastTestedAt)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b" style={{ borderColor: 'var(--color-border)' }}>
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors"
                style={{
                  borderBottomColor: activeTab === tab.id ? 'var(--color-accent)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--color-accent)' : 'var(--color-text-muted)'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                    e.currentTarget.style.borderBottomColor = 'var(--color-border)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                    e.currentTarget.style.borderBottomColor = 'transparent';
                  }
                }}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div 
        className="rounded-xl shadow-sm border p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Enable/Disable */}
              <div className="md:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isEnabled"
                    checked={formData.isEnabled || false}
                    onChange={(e) => handleInputChange('isEnabled', e.target.checked)}
                    className="h-4 w-4 border rounded"
                    style={{
                      color: 'var(--color-accent)',
                      borderColor: 'var(--color-border)'
                    }}
                  />
                  <label htmlFor="isEnabled" className="ml-2 block text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    Enable Email Service
                  </label>
                </div>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  When enabled, the system will send emails for user creation, password changes, etc.
                </p>
              </div>

              {/* SMTP Host */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  SMTP Host
                </label>
                <input
                  type="text"
                  value={formData.smtpHost || ''}
                  onChange={(e) => handleInputChange('smtpHost', e.target.value)}
                  placeholder="smtp.gmail.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-accent)',
                    '--tw-ring-offset-color': 'var(--color-bg-primary)'
                  } as React.CSSProperties}
                />
              </div>

              {/* SMTP Port */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  SMTP Port
                </label>
                <input
                  type="number"
                  value={formData.smtpPort || 587}
                  onChange={(e) => handleInputChange('smtpPort', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-accent)',
                    '--tw-ring-offset-color': 'var(--color-bg-primary)'
                  } as React.CSSProperties}
                />
              </div>

              {/* SMTP Secure */}
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="smtpSecure"
                    checked={formData.smtpSecure || false}
                    onChange={(e) => handleInputChange('smtpSecure', e.target.checked)}
                    className="h-4 w-4 border rounded"
                    style={{
                      color: 'var(--color-accent)',
                      borderColor: 'var(--color-border)'
                    }}
                  />
                  <label htmlFor="smtpSecure" className="ml-2 block text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    Use SSL/TLS
                  </label>
                </div>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Enable for secure connections (usually port 465)
                </p>
              </div>

              {/* SMTP User */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  SMTP Username
                </label>
                <input
                  type="text"
                  value={formData.smtpUser || ''}
                  onChange={(e) => handleInputChange('smtpUser', e.target.value)}
                  placeholder="your-email@gmail.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-accent)',
                    '--tw-ring-offset-color': 'var(--color-bg-primary)'
                  } as React.CSSProperties}
                />
              </div>

              {/* SMTP Password */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  SMTP Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    onChange={(e) => handleInputChange('smtpPassword', e.target.value)}
                    placeholder="Enter SMTP password"
                    className="w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2"
                    style={{
                      backgroundColor: 'var(--color-bg-primary)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                      '--tw-ring-color': 'var(--color-accent)',
                      '--tw-ring-offset-color': 'var(--color-bg-primary)'
                    } as React.CSSProperties}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors"
                    style={{ color: 'var(--color-text-muted)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--color-text-secondary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--color-text-muted)';
                    }}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* From Email */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  From Email Address
                </label>
                <input
                  type="email"
                  value={formData.fromEmail || ''}
                  onChange={(e) => handleInputChange('fromEmail', e.target.value)}
                  placeholder="noreply@yourcompany.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-accent)',
                    '--tw-ring-offset-color': 'var(--color-bg-primary)'
                  } as React.CSSProperties}
                />
              </div>

              {/* From Name */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  From Name
                </label>
                <input
                  type="text"
                  value={formData.fromName || ''}
                  onChange={(e) => handleInputChange('fromName', e.target.value)}
                  placeholder="Provider Selection"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-accent)',
                    '--tw-ring-offset-color': 'var(--color-bg-primary)'
                  } as React.CSSProperties}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'templates' && (() => {
          const templateMeta = TEMPLATE_CONFIG[selectedTemplate];
          const subjectValue = String(formData[templateMeta.subjectKey] ?? '');
          const bodyValue = String(formData[templateMeta.bodyKey] ?? '');

          return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Email Template
              </label>
              <div className="relative">
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value as TemplateType)}
                  className="w-full px-3 py-2 pr-10 border rounded-lg appearance-none focus:ring-2 cursor-pointer"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-accent)',
                    '--tw-ring-offset-color': 'var(--color-bg-primary)'
                  } as React.CSSProperties}
                >
                  {(Object.keys(TEMPLATE_CONFIG) as TemplateType[]).map((key) => (
                    <option key={key} value={key}>
                      {TEMPLATE_CONFIG[key].label}
                    </option>
                  ))}
                </select>
                <ChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none w-4 h-4"
                  style={{ color: 'var(--color-text-muted)' }}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Subject Line
                </label>
                <input
                  type="text"
                  value={subjectValue}
                  onChange={(e) => handleInputChange(templateMeta.subjectKey, e.target.value)}
                  placeholder={templateMeta.subjectPlaceholder}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-accent)',
                    '--tw-ring-offset-color': 'var(--color-bg-primary)'
                  } as React.CSSProperties}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Email Body
                </label>
                <EmailTiptapEditor
                  key={`${selectedTemplate}-${formData.id ?? 'new'}`}
                  editorKey={`${selectedTemplate}-${formData.id ?? 'new'}`}
                  content={bodyValue}
                  onChange={(html) => handleInputChange(templateMeta.bodyKey, html)}
                  placeholder={templateMeta.bodyPlaceholder}
                />
              </div>
            </div>

            <div 
              className="border rounded-lg p-4"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)'
              }}
            >
              <div className="flex items-start">
                <Edit3 className="w-5 h-5 mt-0.5 mr-3" style={{ color: 'var(--color-accent)' }} />
                <div>
                  <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Template Variables</h4>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Use single braces in subjects and body: <code style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', padding: '2px 4px', borderRadius: '3px' }}>{'{firstName}'}</code>, <code style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', padding: '2px 4px', borderRadius: '3px' }}>{'{lastName}'}</code>, 
                    <code style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', padding: '2px 4px', borderRadius: '3px' }}>{'{username}'}</code>, <code style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', padding: '2px 4px', borderRadius: '3px' }}>{'{email}'}</code>, <code style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', padding: '2px 4px', borderRadius: '3px' }}>{'{companyName}'}</code> (currently: {settings?.companyName || 'N/A'}), <code style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', padding: '2px 4px', borderRadius: '3px' }}>{'{password}'}</code>, <code style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', padding: '2px 4px', borderRadius: '3px' }}>{'{code}'}</code> (login verification), <code style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', padding: '2px 4px', borderRadius: '3px' }}>{'{resetLink}'}</code>
                  </p>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {activeTab === 'testing' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Test Email Delivery</h3>
              <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                Send a test email to verify your SMTP configuration is working correctly.
              </p>

              <div className="max-w-md">
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Test Email Address
                </label>
                <div className="flex space-x-3">
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2"
                    style={{
                      backgroundColor: 'var(--color-bg-primary)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                      '--tw-ring-color': 'var(--color-accent)',
                      '--tw-ring-offset-color': 'var(--color-bg-primary)'
                    } as React.CSSProperties}
                  />
                  <button
                    onClick={handleTestEmail}
                    disabled={!testEmail || isSendingTest}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {isSendingTest ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Test
                  </button>
                </div>
              </div>

              {/* Test Results */}
              {testEmailResult?.sendTestEmail && (
                <div 
                  className="mt-4 p-4 rounded-lg border"
                  style={{
                    backgroundColor: testEmailResult.sendTestEmail.success 
                      ? 'rgba(34, 197, 94, 0.1)' 
                      : 'rgba(239, 68, 68, 0.1)',
                    borderColor: testEmailResult.sendTestEmail.success 
                      ? 'rgba(34, 197, 94, 0.3)' 
                      : 'rgba(239, 68, 68, 0.3)'
                  }}
                >
                  <div className="flex items-start">
                    {testEmailResult.sendTestEmail.success ? (
                      <CheckCircle className="w-5 h-5 mt-0.5 mr-3" style={{ color: 'var(--color-success)' }} />
                    ) : (
                      <AlertCircle className="w-5 h-5 mt-0.5 mr-3" style={{ color: 'var(--color-error)' }} />
                    )}
                    <div>
                      <p className="font-medium" style={{
                        color: testEmailResult.sendTestEmail.success ? 'var(--color-success)' : 'var(--color-error)'
                      }}>
                        {testEmailResult.sendTestEmail.success ? 'Test Email Sent!' : 'Test Failed'}
                      </p>
                      <p className="text-sm mt-1" style={{
                        color: 'var(--color-text-secondary)'
                      }}>
                        {testEmailResult.sendTestEmail.message}
                      </p>
                      {testEmailResult.sendTestEmail.sentAt && (
                        <p className="text-xs mt-1" style={{
                          color: 'var(--color-text-muted)'
                        }}>
                          Sent at: {formatDate(testEmailResult.sendTestEmail.sentAt)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Last Test Info */}
              {data?.emailConfiguration?.lastTestedAt && (
                <div 
                  className="mt-6 p-4 rounded-lg"
                  style={{ 
                    backgroundColor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)'
                  }}
                >
                  <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Last Test</h4>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Last tested: {formatDate(data.emailConfiguration.lastTestedAt)}
                  </p>
                  {data.emailConfiguration.testEmailSent && (
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      Test email sent: {formatDate(data.emailConfiguration.testEmailSent)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
